import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { animateScroll as scroll } from 'react-scroll';

import { State } from 'src/state/ducks';
import * as bakedBreadActions from 'src/state/ducks/bakedBread/actions';
import * as bakedBreadTagActions from 'src/state/ducks/bakedBreadTag/actions';
import * as notificationActions from 'src/state/ducks/notification/actions';
import * as shopActions from 'src/state/ducks/shop/actions';
import { BakedBreadEntity } from 'src/firebase/db/BakedBreads';
import { OrderBreadEntity } from 'src/firebase/db/OrderBreads';
import { NotificationEntity } from 'src/firebase/db/Notifications';
import { UserRole } from 'src/firebase/db/Admins';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';
import { toaster } from 'src/helpers/UtilityHelper';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import ReservationCreateForm from 'src/components/organisms/form/ReservationCreateForm';
import ReservationConfirmForm from 'src/components/organisms/form/ReservationConfirmForm';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';

type Props = DefaultPageProps &
  HistoryProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ReservationCreatePage = (props: Props) => {
  const {
    isFetching,
    isValidated,
    shops,
    bakedBreadTags,
    bakedBreadDraft,
    orderBreadDraft,
    notificationDraft,
    errors,
    getShops,
    getBakedBreadTags,
    init,
    validate,
    edit,
    create,
  } = props;

  useEffect(() => {
    const fetch = async () => {
      await init();
      await getShops();
      await getBakedBreadTags();
    };
    fetch();
    return () => {
      init();
    };
  }, [getShops, getBakedBreadTags, init]);

  useEffect(() => {
    if (!isValidated || errors.length) {
      scroll.scrollToTop();
    } else {
      window.scrollTo(0, 0);
    }
  }, [errors, isValidated]);

  const onEdit = useCallback(() => {
    scroll.scrollToTop();
    edit();
  }, [edit]);

  const onSave = useCallback(
    (payload: {
      bakedBread: BakedBreadEntity;
      orderBread: OrderBreadEntity;
      notification: NotificationEntity;
    }) => {
      create(payload);
    },
    [create],
  );

  const onValidate = useCallback(
    (payload: {
      bakedBread: BakedBreadEntity;
      orderBread: OrderBreadEntity;
      notification: NotificationEntity;
    }) => {
      validate(payload);
    },
    [validate],
  );

  const onEmpty = useCallback(() => {
    toaster('error', '全て空欄のため、下書き保存ができません。');
  }, []);

  return (
    <DefaultLayout {...props}>
      <Container>
        <SpinnerModal isLoading={isFetching} />

        {isValidated ? (
          <>
            <Heading tag="h1" text="事前予約注文内容のご確認" />
            <Lead>
              <p>予約注文開始日時（注文解禁日）に下記の内容でお知らせを配信いたします。</p>
              <p>内容に間違いがないかご確認の上、配信するボタンを押してください。</p>
              <p className="alert">
                ※事前告知開始日時までは、【配信履歴確認ページ】からキャンセルが可能です
              </p>
            </Lead>
            <ReservationConfirmForm
              bakedBread={bakedBreadDraft}
              orderBread={orderBreadDraft}
              notification={notificationDraft}
              onSubmit={onSave}
              onBack={onEdit}
              onCancel={onEmpty}
            />
          </>
        ) : (
          <>
            <Heading tag="h1" text="事前予約注文を受けるアイテムの設定する" />
            <ReservationCreateForm
              shops={shops}
              bakedBreadTags={bakedBreadTags}
              bakedBread={bakedBreadDraft}
              orderBread={orderBreadDraft}
              notification={notificationDraft}
              onSubmit={onValidate}
              onSave={onSave}
              onEmpty={onEmpty}
              errors={errors}
            />
          </>
        )}
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M16};
  .order__annotaion {
    font-size: 12px;
    color: ${StyleConfig.COLOR.SECONDORY};
    &.bold {
      font-size: 14px;
      font-weight: bold;
      padding: ${StyleConfig.HELPER.M16};
    }
  }
`;
const Lead = styled.div`
  font-size: 14px;
  line-height: ${24 / 14};

  .alert {
    margin-top: 16px;
    margin-bottom: 24px;
    font-weight: bold;
    color: red;
  }
`;

const mapStateToProps = (state: State) => {
  const {
    admin: {
      user: { shop_ids, role },
    },
    notification: {
      reservations: {
        draft: {
          bakedBread: bakedBreadDraft,
          orderBread: orderBreadDraft,
          notification: notificationDraft,
        },
        isValidated,
        validationErrors: errors,
      },
    },
    shop: { resource: shops },
    bakedBreadTag: { resource: bakedBreadTags },
  } = state;

  return {
    shops:
      role === UserRole.ADMIN && !shop_ids.length
        ? shops
        : shops.filter((shop) => shop_ids.includes(shop.id)),
    bakedBreadTags: bakedBreadTags.filter(
      ({ is_disabled, is_featured }) => !is_disabled && is_featured,
    ),
    bakedBreadDraft,
    orderBreadDraft,
    notificationDraft,
    errors,
    isValidated,
    isFetching:
      state.shop.isFetching ||
      state.bakedBread.isFetching ||
      state.bakedBreadTag.isFetching ||
      state.notification.reservations.isFetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getShop: (shop_id: string) => dispatch(shopActions.getShop(shop_id)),
  getShops: () => dispatch(shopActions.getShops()),
  getBakedBreads: (shop_id: string) => dispatch(bakedBreadActions.getBakedBreads(shop_id)),
  getBakedBreadTags: () => dispatch(bakedBreadTagActions.getBakedBreadTags()),
  // init: () => console.log('init'),
  init: () => dispatch(notificationActions.initReservation()),
  validate: (payload: {
    bakedBread: BakedBreadEntity;
    orderBread: OrderBreadEntity;
    notification: NotificationEntity;
  }) => dispatch(notificationActions.validateReservation(payload)),
  update: (payload: {
    bakedBread: BakedBreadEntity;
    orderBread: OrderBreadEntity;
    notification: NotificationEntity;
  }) => dispatch(notificationActions.updateReservation(payload)),
  create: (payload: {
    bakedBread: BakedBreadEntity;
    orderBread: OrderBreadEntity;
    notification: NotificationEntity;
  }) => dispatch(notificationActions.createReservation(payload)),
  edit: () => dispatch(notificationActions.editReservation()),
});
export default connect(mapStateToProps, mapDispatchToProps)(ReservationCreatePage);
